// rxAdapters.ts — DTO adapters: DRF prescription rows (snake_case, ISO dates,
// backend status enums) → the UI's RxPrescription shape (en-GB dates, display
// status labels). Mock rows already use the UI shapes and skip these.

import { ukDate } from './stocksAdapters';
import type {
  RxLine,
  RxLineStatus,
  RxPatient,
  RxPrescription,
  RxScanState,
  RxStatus,
} from '@/domain/types';

export interface PrescriptionLineDto {
  id: string;
  sku: string;
  level?: string;
  drug_name: string;
  dosage?: string;
  qty_prescribed: number;
  qty_allocated: number;
  qty_dispensed: number;
  status: string;
  scan_status: string;
  allocation_id: string | null;
  allocated_batch_id: string | null;
  fefo_batch_number: string;
  allocated_batch_number: string;
  expiry_date: string | null;
  qty_owing?: number;
  drug_tariff?: {
    above: boolean;
    price: number;
    dt_reference: number;
    cheapest_maker: string;
  } | null;
  allocations?: Array<{
    allocation_id: string;
    batch_id: string;
    batch_number: string;
    qty: number;
    units_per_pack: number;
    expiry_date: string | null;
    scan_status: 'not_scanned' | 'match' | 'override' | 'mismatch';
    bin_location: string;
  }>;
}

export interface PrescriptionDto {
  id: string;
  store_id: string;
  nhs_number: string;
  prescription_id: string;
  status: string;
  patient_name: string;
  patient_dob: string | null;
  prescriber: string;
  practice: string;
  source: string;
  token: string;
  is_cd: boolean;
  prescribed_date: string | null;
  raw_payload: Record<string, unknown> | null;
  lines: PrescriptionLineDto[];
  outward_id: string | null;
  /** Physical basket number bound to this script, by either binding path. */
  tray?: string | null;
  /** Collection / dispense-bench location this script is assigned to. */
  collection_location?: string | null;
  outward_status: string | null;
  owing?: {
    id: string;
    status: string;
    sub_status: string;
    cause: string;
    qty_pending: number;
    qty_fulfilled: number;
  } | null;
  created_at: string;
  clinical_check_by?: string | null;
  clinical_check_at?: string | null;
  accuracy_self_checked?: boolean;
  clinical_check_notes?: string;
  revalidation_mode?: 'full' | 'risk_based';
  query_reason?: string;
  rejection_reason?: string;
}

const RX_STATUS: Record<string, RxStatus> = {
  fetched: 'New',
  awaiting_clinical_check: 'Awaiting Clinical Check',
  query_raised: 'Query Raised',
  rejected: 'Rejected',
  awaiting_allocation: 'Awaiting Allocation',
  allocated: 'Allocated',
  picking: 'Picking',
  awaiting_accuracy_check: 'Awaiting Accuracy Check',
  ready_for_dispense: 'Ready for Dispense',
  ready_for_collection: 'Awaiting Collection',
  collected: 'Collected',
  partially_dispensed: 'Partially Dispensed',
  owing: 'Owing',
  incomplete: 'Incomplete',
  dispensed: 'Dispensed',
  closed: 'Fulfilled',
  notified: 'Fulfilled',
  cancelled: 'Cancelled',
};

const LINE_STATUS: Record<string, RxLineStatus> = {
  awaiting_allocation: 'Awaiting Allocation',
  allocated: 'Allocated',
  picked: 'Picked',
  owing: 'Owing',
  product_not_available: 'Product Not Available',
  dispensed: 'Dispensed',
  incomplete: 'Incomplete',
  cancelled: 'Cancelled',
};

const SCAN_STATUS: Record<string, RxScanState> = {
  not_scanned: 'Not Scanned',
  match: 'Match',
  mismatch: 'Mismatch',
};

// Friendly label for where an owed item is in its lifecycle (shown in the Rx Overview
// and the Dispense Queue).
export function owingStageLabel(status: string, subStatus: string, cause: string): string {
  switch (status) {
    case 'stock_received':
      return 'Stock received — re-allocating';
    case 'ready_to_pick':
      return 'Re-pick ready — back in the dispense queue';
    case 'ready_for_collection':
      return 'Owed item in the collection area — ready for collection';
    case 'fulfilled':
      return subStatus === 'awaiting_patient_collection'
        ? 'Owed stock ready — awaiting patient collection'
        : 'Fulfilled — re-entering dispense';
    case 'partially_fulfilled':
      return 'Partially fulfilled — remainder owed';
    default: // open
      if (subStatus === 'awaiting_patient_collection' || cause === 'owing')
        return 'Owing — awaiting stock for the owed quantity';
      return 'Incomplete — awaiting supplier';
  }
}

function lineToUi(dto: PrescriptionLineDto): RxLine {
  return {
    id: dto.id,
    drug: dto.drug_name || dto.sku,
    sku: dto.sku,
    level: (dto.level as RxLine['level']) ?? 'ampp',
    dosage: dto.dosage || '',
    qty: dto.qty_prescribed,
    fefoBatch: dto.fefo_batch_number || null,
    allocBatch: dto.allocated_batch_number || null,
    allocId: dto.allocation_id,
    allocQty: dto.qty_allocated,
    exp: dto.expiry_date ? ukDate(dto.expiry_date) : null,
    status: LINE_STATUS[dto.status] ?? 'Awaiting Allocation',
    scan: SCAN_STATUS[dto.scan_status] ?? 'Not Scanned',
    partPack: false,
    dispensedQty: dto.qty_dispensed,
    owingQty: Math.max(0, dto.qty_prescribed - dto.qty_dispensed),
    batchStockId: dto.allocated_batch_id,
    drugTariff: dto.drug_tariff ?? null,
  };
}

export function prescriptionToRx(raw: unknown): RxPrescription {
  const dto = raw as PrescriptionDto;
  const notification = (dto.raw_payload?.eps_notification ?? null) as {
    reference_id?: string;
    status?: string;
  } | null;
  const status = RX_STATUS[dto.status] ?? 'New';
  const notified = ['Dispensed', 'Partially Dispensed', 'Fulfilled', 'Owing'].includes(status);
  return {
    id: dto.prescription_id,
    uuid: dto.id,
    patient: dto.patient_name || '—',
    nhs: dto.nhs_number,
    dob: dto.patient_dob ? ukDate(dto.patient_dob) : '—',
    prescriber: dto.prescriber || '—',
    practice: dto.practice || '—',
    rxDate: ukDate(dto.prescribed_date ?? dto.created_at),
    source: dto.source === 'manual' ? 'Manual' : 'EPS',
    token: dto.token || '—',
    store: dto.store_id,
    cd: dto.is_cd,
    status,
    spineNotified: notification
      ? `Queued (${notification.status ?? 'unsynced'})`
      : notified
        ? status === 'Dispensed' || status === 'Fulfilled'
          ? 'Fulfilled'
          : 'Partial'
        : null,
    ref: notification?.reference_id,
    manualFlag: dto.source === 'manual',
    clinicalCheckBy: dto.clinical_check_by ?? null,
    clinicalCheckAt: dto.clinical_check_at ?? null,
    accuracySelfChecked: !!dto.accuracy_self_checked,
    clinicalCheckNotes: dto.clinical_check_notes ?? '',
    revalidationMode: dto.revalidation_mode ?? 'full',
    queryReason: dto.query_reason ?? '',
    rejectionReason: dto.rejection_reason ?? '',
    // Server-resolved: covers both the outward-bound (standard) and patient-pre-bound
    // (print-and-basket) binding paths.
    tray: dto.tray ?? null,
    collectionLocation: dto.collection_location ?? null,
    ticket: null,
    outwardId: dto.outward_id,
    outwardStatus: dto.outward_status,
    owing: dto.owing
      ? {
          status: dto.owing.status,
          subStatus: dto.owing.sub_status,
          cause: dto.owing.cause,
          qtyPending: dto.owing.qty_pending,
          qtyFulfilled: dto.owing.qty_fulfilled,
          stageLabel: owingStageLabel(dto.owing.status, dto.owing.sub_status, dto.owing.cause),
        }
      : null,
    lines: (dto.lines ?? []).map(lineToUi),
  };
}

export interface PatientDto {
  nhs_number: string;
  patient_id?: string;
  patient_name: string;
  patient_dob: string | null;
  store_id: string;
  prescription_count: number;
  prescriptions: {
    id: string;
    prescription_id: string;
    status: string;
    collection_location?: string;
  }[];
}

export function patientToUi(dto: PatientDto): RxPatient {
  return {
    key: dto.patient_id || dto.nhs_number,
    nhs: dto.nhs_number,
    name: dto.patient_name || '—',
    dob: dto.patient_dob ? ukDate(dto.patient_dob) : '—',
    store: dto.store_id,
    prescriptionRefs: dto.prescriptions.map((p) => p.prescription_id),
    scripts: dto.prescriptions.map((p) => ({
      id: p.id,
      ref: p.prescription_id,
      status: RX_STATUS[p.status] ?? p.status,
      location: p.collection_location ?? '',
    })),
  };
}
